/**
 * POI sites (plan §5 R4; spec §6.2): the generator's points of interest in the system frame, with their placeholder
 * asset, a seeded yaw and seed, and the side a hidden base belongs to. POIs never sit inside a body's shell (plus
 * `clearanceM`) or near the star: they are pushed out radially.
 */
import { range, type Rng } from '../../core/rng'
import { deriveSeed } from '../seed'
import type { StarSystem } from '../types'
import type { ReachSystemsConfig } from './config'
import { add, distance, galaxyToWorld, length, normalize, scale, sub, yawQuat } from './frame'
import type { BodySite, PoiSite, StarSite, Vec3T } from './types'

const PUSH_PASSES = 8

/**
 * Pushes `p` radially out of the star's clearance sphere and out of every shell + `clearanceM`, repeating until no
 * sphere contains it (a push out of one sphere can land in another).
 */
export function pushClear(p: Vec3T, bodies: readonly BodySite[], star: StarSite, cfg: ReachSystemsConfig): Vec3T {
  const starClear = cfg.pois.starClearanceFactor * star.radiusM
  let out = p
  for (let pass = 0; pass < PUSH_PASSES; pass++) {
    let moved = false
    if (length(out) < starClear) {
      out = scale(normalize(out, [1, 0, 0]), starClear)
      moved = true
    }
    for (const b of bodies) {
      const min = b.shellM + cfg.pois.clearanceM
      if (distance(out, b.position) < min) {
        out = add(b.position, scale(normalize(sub(out, b.position), normalize(b.position, [1, 0, 0])), min))
        moved = true
      }
    }
    if (!moved) break
  }
  return out
}

/** The side a hidden base belongs to: guardians in the guardian-base anchor and in descendant enclaves. */
export function baseSideOf(system: Pick<StarSystem, 'anchor' | 'archetype'>): 'raider' | 'guardian' {
  return system.anchor === 'guardian-base' || system.archetype === 'descendant-enclave' ? 'guardian' : 'raider'
}

/** The generator's POIs as sites, in order (index = the generator's POI index). */
export function placePois(
  galaxySeed: number,
  system: StarSystem,
  bodies: readonly BodySite[],
  star: StarSite,
  cfg: ReachSystemsConfig,
  rng: Rng,
): PoiSite[] {
  return system.pois.map((poi, index): PoiSite => {
    const side = poi.kind === 'hidden-base' ? baseSideOf(system) : null
    return {
      id: poi.id,
      index,
      kind: poi.kind,
      position: pushClear(galaxyToWorld(poi.position), bodies, star, cfg),
      rotation: yawQuat(range(rng, 0, Math.PI * 2)),
      hidden: poi.hidden,
      assetId: side === 'guardian' ? cfg.pois.guardianBaseAsset : cfg.pois.assetByKind[poi.kind],
      seed: deriveSeed(galaxySeed, 'layout', system.index, 'poi', index),
      baseSide: side,
    }
  })
}
